import { events } from "@constants/tracking";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { filterPlans } from "@helpers/content/tag";
import i18n from "@helpers/lib/i18n";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { useProductDetails, useProductMetadata } from "@hooks/useProduct";
import {
	getPremiumContext,
	getRecommendedPlan,
} from "@memorang/helpers/bundle";
import type { Plan, ProductType } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import Button from "@memorang/ui/components/Button";
import Container from "@memorang/ui/components/Container";
import Loader from "@memorang/ui/components/Loader";
import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ScrollView } from "react-native";
import { Text } from "react-native-paper";
import IAPModalView from "../../components/IAPModal/IAPModalView";
import { getExamDaysLeft } from "../../helpers/stats";
import { filterPlansByExamDate, hasPurchaseBundleData } from "./helpers";

type PurchaseContainerBaseProps = {
	handleClose?: () => void;
	fromHomePage?: boolean;
	productType: ProductType;
	handlePurchase: (
		productNumericId: number,
		productId: string,
		selectedPlan?: Plan,
	) => void;
	handleShowRestorePurchaseDialog: () => void;
	purchaseInProgress: boolean;
	explicitProductNumericId?: number;
	explicitProductId?: string;
};

const PurchaseContainerBase = ({
	handleClose,
	fromHomePage,
	productType,
	handlePurchase,
	handleShowRestorePurchaseDialog,
	purchaseInProgress,
	explicitProductNumericId,
	explicitProductId,
}: PurchaseContainerBaseProps) => {
	const { numItems } = useLocalSearchParams();

	const { explicitCallTrackCustomEvent } = useAnalytics();
	const isSubscriptionEnabled = useAppStore(
		(store) => store.app.purchase?.isSubscriptionEnabled,
	);

	const { data: productDetails, isLoading } = useProductDetails(
		productType,
		explicitProductNumericId,
	);

	const { data: productMetadata, isLoading: isProductMetadataLoading } =
		useProductMetadata(productType, explicitProductId);

	const productId = productDetails?.logicalId;
	const name = productDetails?.name;

	const plans = useMemo(() => productDetails?.plans, [productDetails]);

	const filteredPlans = useMemo(() => {
		return plans ? filterPlans(plans) : [];
	}, [plans]);

	useEffect(() => {
		if (productDetails) {
			explicitCallTrackCustomEvent({
				eventName: events.paywallOpened,
				plans: JSON.stringify(productDetails.plans),
				product_id: productDetails.id,
				product_name: productDetails.name,
			});
		}
	}, [productDetails, explicitCallTrackCustomEvent]);

	const handleTracking = useCallback(
		(eventName: string, params?: Record<string, string | number>) => {
			explicitCallTrackCustomEvent({
				eventName,
				product_id: productId,
				product_name: name,
				...params,
			});
		},
		[productId, name, explicitCallTrackCustomEvent],
	);

	const {
		currentExamContext: { examDate },
	} = useExamContext();

	const daysLeft = getExamDaysLeft(examDate);

	const sortedPlans = useMemo(() => {
		return filteredPlans
			? filterPlansByExamDate(
					[...filteredPlans].sort((a, b) => a.amount - b.amount),
					daysLeft,
				)
			: [];
	}, [daysLeft, filteredPlans]);

	const [selectedPlan, setSelectedPlan] = useState(
		() => getRecommendedPlan(sortedPlans, daysLeft) || sortedPlans?.[0],
	);

	useEffect(() => {
		if (sortedPlans.length > 0) {
			setSelectedPlan(
				getRecommendedPlan(sortedPlans, daysLeft) || sortedPlans?.[0],
			);
		}
	}, [sortedPlans, daysLeft]);

	if (isLoading || isProductMetadataLoading) {
		return (
			<Box flex={1} justifyContent="center" alignItems="center">
				<Loader />
			</Box>
		);
	}

	if (!hasPurchaseBundleData(productDetails)) {
		return (
			<Container maxWidth="md">
				<Box
					padding={16}
					gap={12}
					alignItems="center"
					justifyContent="center"
					flex={1}
				>
					<Text variant="bodyMedium" style={{ textAlign: "center" }}>
						Unable to load purchase options right now. Please try again.
					</Text>
					{handleClose ? (
						<Button mode="contained" onPress={handleClose}>
							Close
						</Button>
					) : null}
				</Box>
			</Container>
		);
	}

	const { features, featuresMandarin, featuresJapanese, icon, numericId } =
		productDetails;
	const url = icon?.url ?? "";

	const metadata = productMetadata;
	const premiumContext = metadata ? getPremiumContext(metadata) : null;
	const hasAccess = premiumContext?.hasAccess;

	const recommendedPlan = getRecommendedPlan(sortedPlans, daysLeft);

	const handleClickAccessHere = () => {
		handleClose?.();
		router.push("/home");
	};

	const getFeatures = () => {
		switch (i18n.language) {
			case "zh-CN":
				return featuresMandarin;
			case "ja-JP":
				return featuresJapanese;
			default:
				return features;
		}
	};

	//TODO:Need to be better handled
	const finalFeatures = getFeatures();

	return (
		<Container
			maxWidth="md"
			style={{
				//@ts-ignore - web only
				overflow: "auto",
			}}
		>
			<ScrollView
				contentContainerStyle={{
					flexGrow: 1,
					paddingBottom: 24,
				}}
			>
				<IAPModalView
					handleRestorePurchase={handleShowRestorePurchaseDialog}
					features={finalFeatures || []}
					url={url}
					productType={productType || "STUDY_PACK"}
					recommendedPlan={recommendedPlan}
					selectedDate={examDate}
					hasAccess={hasAccess}
					handleTracking={handleTracking}
					selectedPlan={selectedPlan}
					handleClickAccessHere={handleClickAccessHere}
					expiryDate={metadata?.subscription?.endDate}
					setSelectedPlan={setSelectedPlan}
					loading={purchaseInProgress}
					disabled={purchaseInProgress || !selectedPlan}
					handlePurchase={() =>
						handlePurchase(numericId, productDetails.logicalId, selectedPlan)
					}
					sortedPlans={sortedPlans}
					purchasable={true}
					numItems={numItems ? Number(numItems) : 0}
					fromHomePage={fromHomePage}
					isSubscriptionEnabled={isSubscriptionEnabled}
				/>
			</ScrollView>
		</Container>
	);
};

export default PurchaseContainerBase;
